import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideRouter, withPreloading } from '@angular/router';
import type { editor as Monaco } from 'monaco-editor';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';

import { authInterceptorFn, AuthModule } from '@plugins/client/auth';
import { JsonDateModule } from '@plugins/client/json-date';
import { AdminPreloadStrategy } from '@plugins/client/routing/admin-preload-strategy';
import { provideAppVersion } from '@plugins/client/tokens/app-version.token';
import { localeIdProvider } from '@plugins/client/translate/providers';
import {
  TRANSLATE_CONFIG,
  TranslateConfigToken,
} from '@plugins/client/translate/services';
import { MiddlewareSdkModule } from '@plugins/middleware-sdk';
import flowSchema from '@plugins/shared/validators/schemas/flow.schema.json';

import { environment } from '../environments/environment';

import { routes } from './app.routes';
import { provideJsonFormsRenderers } from './renderers';

// declare monaco editor in window
declare global {
  interface Window {
    monaco: typeof import('monaco-editor');
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withPreloading(AdminPreloadStrategy)),
    provideHttpClient(withInterceptors([authInterceptorFn])),
    importProvidersFrom(MiddlewareSdkModule),
    importProvidersFrom(AuthModule.forRoot()),
    importProvidersFrom(JsonDateModule.configure({ paths: ['/api/'] })),
    localeIdProvider(environment.languages),
    provideAppVersion(),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: TRANSLATE_CONFIG,
      useValue: {
        languages: environment.languages,
        fallbackLanguage: environment.languages[0],
        useFallbackLanguageOnMissing: true,
      } satisfies TranslateConfigToken,
    },
    importProvidersFrom(
      MonacoEditorModule.forRoot({
        baseUrl: window.location.origin + '/assets/monaco/min/vs',
        defaultOptions: {
          theme: 'vs-dark',
          minimap: { enabled: false },
          formatOnType: true,
          formatOnPaste: true,
          automaticLayout: true,
          scrollBeyondLastLine: false,
          scrollbar: {
            alwaysConsumeMouseWheel: false,
          },
        } as Monaco.IStandaloneEditorConstructionOptions,
        onMonacoLoad: () => {
          (
            window as any
          ).monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
            validate: true,
            schemaValidation: 'error',
            schemas: [
              {
                uri: 'http://flow-schema.json',
                fileMatch: ['inmemory://flow-schema.json'],
                schema: flowSchema,
              },
            ],
          });
        },
      }),
    ),
    provideJsonFormsRenderers(),
  ],
};
